import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useScrollDirection } from "../../hooks/hooks";
import { FiMenu, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import logo from "../../pictures/logo.jpg";

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scrollDirection = useScrollDirection();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container") && activeDropdown) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeDropdown]);

  // Close mobile menu when clicking on a link
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileDropdown(null);
  };

  // Navigation items with dropdowns
  const navItems = [
    { name: "Home", href: "/" },
    {
      name: "About Us",
      subItems: [
        { name: "About Doctus", href: "/about" },
        { name: "Management Team", href: "/management" },
        { name: "Infrastructure", href: "/infrastructure" },
        { name: "How We Deliver", href: "/delivery" },
      ],
    },
    {
      name: "Services",
      subItems: [
        { name: "Revenue Cycle Services", href: "/revenue-cycle" },
        { name: "Medical Coding Services", href: "/medical-coding" },
        // { name: "Payer Services", href: "/payer-services" },
        // { name: "HCC Coding Services", href: "/hcc-coding" },
        // { name: "Home Health Billing Services", href: "/home-health" },
        // { name: "HEDIS Measures Reporting", href: "/hedis" },
        { name: "Document Management Services", href: "/document-management" },
      ],
    },
    // { name: "Blog", href: "/blog" },
    // { name: "The Doctus Advantage", href: "/advantage" },
    // { name: "Gallery", href: "/gallery" },
    {
      name: "Careers",
      subItems: [
        { name: "Working with Doctus", href: "/working" },
        // { name: "Jobs in India", href: "/jobs-india" },
        // { name: "Jobs in Manila", href: "/jobs-manila" },
      ],
    },
  ];

  const toggleMobileDropdown = (itemName) => {
    setMobileDropdown(mobileDropdown === itemName ? null : itemName);
  };

  return (
    <motion.header
      className="bg-white shadow-md fixed w-full z-50"
      style={{ height: "96px" }}
      animate={{
        y: scrollDirection === "down" ? -100 : 0,
      }}
      transition={{ type: "spring", damping: 20, stiffness: 300 }}
    >
      <div className="container mx-auto px-4 py-3 flex justify-between items-center h-full">
        {/* Logo */}
        <div className="flex-shrink-0">
          <Link to="/">
            <img
              src={logo}
              alt="Doctus Logo"
              className="h-20"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => (
            <div key={item.name} className="relative dropdown-container">
              {item.subItems ? (
                <div
                  className="group"
                  onMouseEnter={() => setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="text-black hover:text-gray-600 transition-colors font-medium flex items-center">
                    {item.name}
                    <FiChevronDown className="ml-1" size={14} />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-2 w-60 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-100"
                      >
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  to={item.href}
                  className="text-black hover:text-gray-600 transition-colors font-medium"
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}

          {/* Contact Button */}
          <Link
            to="/contact"
            className="bg-[#4b675c] text-white px-4 py-2 rounded-md font-medium hover:bg-[#4c5463] transition-colors"
          >
            Contact
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-black focus:outline-none"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white overflow-hidden"
          >
            <div className="px-4 py-2 space-y-2">
              {navItems.map((item) => (
                <div key={item.name} className="dropdown-container">
                  {item.subItems ? (
                    <div>
                      <button
                        className="w-full text-left py-2 px-3 text-black font-medium flex justify-between items-center"
                        onClick={() => toggleMobileDropdown(item.name)}
                      >
                        {item.name}
                        {mobileDropdown === item.name ? (
                          <FiChevronUp size={16} />
                        ) : (
                          <FiChevronDown size={16} />
                        )}
                      </button>

                      <AnimatePresence>
                        {mobileDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="pl-4 space-y-1 overflow-hidden"
                          >
                            {item.subItems.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className="block py-2 text-gray-700 hover:text-black"
                                onClick={closeMobileMenu}
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-2 px-3 text-black font-medium hover:bg-gray-50 rounded"
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              <Link
                to="/contact"
                className="block bg-[#4b675c] text-white px-4 py-2 rounded-md font-medium text-center mt-2 hover:bg-[#4b675c]"
                onClick={closeMobileMenu}
              >
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
